import { Slot } from '@radix-ui/react-slot';
import clsx from 'clsx';

// Solid gold, navy text — reserved for the single highest-value action on a page.
const ACCENT = [
  'bg-hhp-gold text-hhp-navy font-bold',
  'hover:bg-hhp-gold/90 active:brightness-95',
  'focus-visible:ring-hhp-gold/50',
].join(' ');

const variants = {
  // Solid navy — the one commit action per screen (Save, Send, Book).
  primary: [
    'bg-hhp-navy-text text-white font-bold',
    'hover:bg-hhp-navy-text-deep active:brightness-95',
    'focus-visible:ring-hhp-navy-text/40',
  ].join(' '),

  accent: ACCENT,
  // Deprecated alias — old call sites used variant="copper" as their de-facto accent button.
  copper: ACCENT,

  // Outline navy — cancel, back, low-commitment steps.
  secondary: [
    'bg-hhp-white text-hhp-navy-text font-medium',
    'border border-hhp-border hover:border-hhp-navy-text/30 hover:bg-hhp-cream',
    'focus-visible:ring-hhp-navy-text/25',
  ].join(' '),

  // Text-only — tertiary actions inside dense rows/tables.
  ghost: [
    'bg-transparent text-hhp-muted font-medium',
    'hover:bg-hhp-cream hover:text-hhp-navy-text',
    'focus-visible:ring-hhp-navy-text/20',
  ].join(' '),

  // Outline danger — fills solid only on hover/confirm. Delete, mark lost.
  danger: [
    'bg-transparent text-hhp-danger font-semibold',
    'border border-hhp-danger/35 hover:bg-hhp-danger hover:text-white hover:border-hhp-danger',
    'focus-visible:ring-hhp-danger/40',
  ].join(' '),
};

const sizes = {
  xs: 'px-2.5 py-1 text-xs rounded-lg',
  sm: 'px-3 py-1.5 text-xs rounded-lg',
  md: 'px-4 py-2 text-sm rounded-xl',
  lg: 'px-6 py-3 text-base rounded-xl',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  asChild?: boolean;
  loading?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  asChild = false,
  loading = false,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      className={clsx(
        'inline-flex items-center justify-center transition-all duration-200',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-hhp-white',
        'disabled:pointer-events-none disabled:opacity-40',
        variants[variant],
        sizes[size],
        className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <>
          <svg
            className="mr-2 h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading…
        </>
      ) : (
        children
      )}
    </Comp>
  );
}
